/**
 * Loyalty Admin Services
 * All loyalty admin services extend BaseService and wrap the NestJS loyalty/admin/* endpoints.
 */

import { BaseService } from 'core/services/BaseService';
import type {
  Influencer, InfluencerListParams,
  KycDocument, KycDocumentAdmin, PointTransaction,
  CouponBatch, CreateCouponBatchDto, BulkCreateCouponBatchDto,
  Gift, CreateGiftDto,
  CashRewardSlab, CreateCashRewardSlabDto, UpdateCashRewardSlabDto,
  Redemption, RedemptionStats,
  GiftType, WalletBalance, WalletLedgerEntry,
  FlaggedScan, FraudStats,
  PurchaseReceipt,
  Campaign, CreateCampaignDto,
  BonusScheme, CreateBonusSchemeDto,
  EngagementScore, Segment, CreateSegmentDto,
  AnalyticsOverview, TrendDataPoint,
  PaginatedResponse,
  ReferralAdminResponse,
  GameConfig, CreateGameDto,
  TierConfig, UpdateTierConfigDto,
  WorkShowcase, WorkShowcaseStats, WorkShowcaseListParams,
} from '../types';
import { PayoutStatus } from '../types';

import type {
  LabelTemplate,
  CreateLabelTemplateDto,
  UpdateLabelTemplateDto,
  LabelTemplateListParams,
} from '../types/labelTemplate';

/**
 * Origin that serves `/uploads/<key>`, where the generated coupon PDFs land.
 *
 * `REACT_APP_API_URL` may be RELATIVE: digital's web image is built with
 * `/api/` so one bundle serves whichever host it is opened on. `new URL('/api/')`
 * with no base throws "Failed to construct 'URL': Invalid URL", which is how
 * printing a batch on Soncera failed (2026-09-29) AFTER the server had already
 * generated the PDF. The page's own origin is the base: it is ignored for an
 * absolute URL (kodia's build), so those builds resolve exactly as before.
 */
const uploadsOrigin = (): string =>
  new URL(process.env.REACT_APP_API_URL || 'http://localhost:8008/api', window.location.origin).origin;

export interface SendPayoutResp {
  urn: string;
  status: PayoutStatus;
}

// ─────────────────────────────────────────────────────────────────────────────
// Influencer Service
// ─────────────────────────────────────────────────────────────────────────────

class InfluencerAdminService extends BaseService<Influencer> {
  constructor() {
    super('loyalty/admin/influencers', 'LOYALTY-INFLUENCERS');
  }

  async getInfluencers(params?: InfluencerListParams): Promise<PaginatedResponse<Influencer>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async getInfluencer(id: string): Promise<Influencer> {
    const res = await this.get(`/${id}`);
    return res.data ?? res;
  }

  // Used by the master-data customer detail page where the URL carries the
  // customer_id (not the influencer_id). Backend matches against inf.customer_id.
  async getInfluencerByCustomerId(customerId: string): Promise<Influencer> {
    const res = await this.get(`/by-customer/${customerId}`);
    return res.data ?? res;
  }

  async getKycDocuments(influencerId: string): Promise<KycDocument[]> {
    const res = await this.get(`/${influencerId}/kyc`);
    return res.data ?? res;
  }

  /**
   * Read-only view of a customer's KYC docs for the InfluencerNetwork detail
   * panel. Endpoint sits on the same plural `/loyalty/admin/influencers`
   * baseURL as `getInfluencerByCustomerId` above — use the same `by-customer/`
   * segment convention. Returns an empty array when the customer has no docs
   * submitted yet.
   */
  async getKycDocumentsByCustomerId(customerId: string): Promise<KycDocumentAdmin[]> {
    const res = await this.get(`/by-customer/${customerId}/kyc-documents`);
    const data = res.data ?? res;
    return Array.isArray(data) ? data : [];
  }

  async approveKyc(influencerId: string): Promise<KycDocument> {
    const res = await this.patch(`/${influencerId}/kyc/approve`, {});
    return res.data ?? res;
  }

  /**
   * Per-document verify — used by KycDocumentsPanel action buttons on PENDING
   * doc tiles. Backend endpoint (commit C10):
   *   PATCH /loyalty/admin/influencers/customer-documents/:documentId/verify
   * The service baseURL is `loyalty/admin/influencers`, so the `/customer-documents/…`
   * relative path resolves to the correct URL.
   */
  async verifyKycDocument(
    documentId: string,
  ): Promise<{ id: string; verificationStatus: 'VERIFIED' }> {
    const res = await this.patch(`/customer-documents/${documentId}/verify`, {});
    return res.data ?? res;
  }

  /**
   * Per-document reject — mirror of verifyKycDocument. Reason is required by the
   * backend DTO.
   *   PATCH /loyalty/admin/influencers/customer-documents/:documentId/reject
   */
  async rejectKycDocument(
    documentId: string,
    reason: string,
  ): Promise<{ id: string; verificationStatus: 'REJECTED' }> {
    const res = await this.patch(`/customer-documents/${documentId}/reject`, { reason });
    return res.data ?? res;
  }

  async rejectKyc(influencerId: string, reason: string): Promise<KycDocument> {
    const res = await this.patch(`/${influencerId}/kyc/reject`, { reason });
    return res.data ?? res;
  }

  async deactivate(id: string | number): Promise<any> {
    return this.patch(`/${id}/deactivate`, {});
  }

  async reactivate(id: string | number): Promise<any> {
    return this.patch(`/${id}/activate`, {});
  }

  async getTransactions(influencerId: string, page = 1, limit = 20): Promise<PaginatedResponse<PointTransaction>> {
    const res = await this.get(`/${influencerId}/transactions`, { params: { page, limit } });
    return res.data ?? res;
  }

  /** Per-influencer scan history — Wallet Transaction → Scan History tab. */
  async getScans(influencerId: string, page = 1, limit = 20): Promise<PaginatedResponse<any>> {
    const res = await this.get(`/${influencerId}/scans`, { params: { page, limit } });
    return res.data ?? res;
  }

  /** Per-influencer redemption history — Wallet Transaction → Redeem History tab. */
  async getRedemptions(influencerId: string, page = 1, limit = 20): Promise<PaginatedResponse<any>> {
    const res = await this.get(`/${influencerId}/redemptions`, { params: { page, limit } });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Coupon Service
// ─────────────────────────────────────────────────────────────────────────────

/** Blob → text. `Blob.text()` where available, FileReader otherwise (older WebViews, jsdom). */
const readBlobText = (blob: Blob): Promise<string> =>
  typeof (blob as any).text === 'function'
    ? (blob as any).text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

class CouponAdminService extends BaseService<CouponBatch> {
  constructor() {
    super('loyalty/admin/coupons', 'LOYALTY-COUPONS');
  }

  async getBatches(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CouponBatch>> {
    const res = await this.get('/batches', { params });
    return res.data ?? res;
  }

  async createBatch(dto: CreateCouponBatchDto): Promise<CouponBatch> {
    const res = await this.post('/batches', dto);
    return res.data ?? res;
  }

  /** Create one batch per product, all or nothing. */
  async createBatchesBulk(dto: BulkCreateCouponBatchDto): Promise<CouponBatch[]> {
    const res = await this.post('/batches/bulk', dto);
    return res.data ?? res;
  }

  async toggleScan(batchId: string, enabled: boolean): Promise<CouponBatch> {
    const endpoint = enabled ? `/batches/${batchId}/enable` : `/batches/${batchId}/disable`;
    const res = await this.patch(endpoint, {});
    return res.data ?? res;
  }

  async dispatch(batchId: string): Promise<CouponBatch> {
    const res = await this.post(`/batches/${batchId}/dispatch`, {});
    return res.data ?? res;
  }

  async getCoupons(batchId: string, params?: { page?: number; limit?: number; status?: string }): Promise<{
    items: any[]; total: number; page: number; limit: number;
  }> {
    const res = await this.get(`/batches/${batchId}/coupons`, { params });
    return res.data ?? res;
  }

  /**
   * "Scanned QR Code" tab on the Coupon Batch Master page — org-scoped audit
   * list of every scan event, one row per loyalty_scans record. Filters:
   * search (matches qrCode / scanner name / mobile), dateFrom / dateTo (ISO)
   * on scan timestamp. `couponBatchId` optionally scopes to one batch.
   */
  async getScannedQrs(params?: {
    page?: number;
    limit?: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    couponBatchId?: string;
    productId?: string;
  }): Promise<{
    items: Array<{
      qrCode: string;
      productDetail: string;
      pointDetail: string;
      couponValue: number;
      scanBy: string;
      mobile: string;
      scanDate: string;
      bonusName: string | null;
      state: string | null;
      district: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const res = await this.get('/scanned-qrs', { params });
    return res.data ?? res;
  }

  /**
   * `params` mirrors the "Labels per row" popup on `LabelPrintDialog` — when
   * present, both are forwarded as query params so the backend PDF grid
   * matches the browser-print layout (see coupon.controller.ts export-pdf).
   */
  async downloadQrPdf(
    batchId: string,
    params?: { labelsPerRow?: number; labelRowsPerPage?: number },
  ): Promise<Blob> {
    const res = await this.post(`/batches/${batchId}/export-pdf`, {}, { params });
    const s3Key: string = res?.data?.s3Key ?? res?.s3Key;
    if (!s3Key) throw new Error('PDF generation failed: no s3Key returned');
    const apiBase = uploadsOrigin();
    const fileRes = await fetch(`${apiBase}/uploads/${s3Key}`);
    if (!fileRes.ok) throw new Error('Failed to fetch PDF file');
    return fileRes.blob();
  }

  /**
   * "Multiple Print" — one PDF containing 2..20 batches, each starting on a
   * fresh page with its own sticker-template grid.
   *
   * `batchIds` goes in the body (not query params) because a 20-UUID list
   * overruns sane URL limits. Returns the blob plus the server's counts so the
   * caller can report what actually made it in — batches with zero coupons are
   * skipped server-side, so `batchCount` may be lower than `batchIds.length`.
   */
  async downloadQrPdfMultiple(
    batchIds: string[],
    params?: { labelsPerRow?: number; labelRowsPerPage?: number },
  ): Promise<{ blob: Blob; batchCount: number; couponCount: number }> {
    const res = await this.post('/batches/export-pdf-multi', {
      batchIds,
      ...(params?.labelsPerRow ? { labelsPerRow: params.labelsPerRow } : {}),
      ...(params?.labelRowsPerPage ? { labelRowsPerPage: params.labelRowsPerPage } : {}),
    });
    const payload = res?.data ?? res;
    const s3Key: string = payload?.s3Key;
    if (!s3Key) throw new Error('PDF generation failed: no s3Key returned');
    const apiBase = uploadsOrigin();
    const fileRes = await fetch(`${apiBase}/uploads/${s3Key}`);
    if (!fileRes.ok) throw new Error('Failed to fetch PDF file');
    return {
      blob: await fileRes.blob(),
      batchCount: payload?.batchCount ?? batchIds.length,
      couponCount: payload?.couponCount ?? 0,
    };
  }

  /**
   * Coupon-batch Excel download (CT-539837) — one row per coupon in the picked
   * batches and/or batches created in [dateFrom, dateTo]. At least one filter
   * is required (the backend rejects an empty one).
   *
   * Errors come back as a JSON body even though we asked for a blob, so the
   * server's message ("No coupons found…", "more than 100,000…") is unwrapped
   * into the thrown Error for the caller to toast.
   */
  async downloadBatchesExcel(filter: {
    batchIds?: string[];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Blob> {
    try {
      const res = await this.post('/batches/export-excel', filter, { responseType: 'blob' });
      const blob: Blob = res?.data ?? res;
      if (!(blob instanceof Blob)) throw new Error('Excel export failed: no file returned');
      return blob;
    } catch (err: any) {
      const body = err?.response?.data;
      let serverMessage: string | undefined;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await readBlobText(body));
          serverMessage = Array.isArray(parsed?.message) ? parsed.message[0] : parsed?.message;
        } catch {
          // Not JSON (e.g. an HTML 502 from the proxy) — fall through to the original error.
        }
      }
      if (serverMessage) throw new Error(serverMessage);
      throw err;
    }
  }

  async getCouponLimit(): Promise<{ maxCouponCodes: number | null }> {
    const res = await this.get('/limit');
    return res.data ?? res;
  }

  /**
   * Widen-only: adds roles to an already-printed batch's target set. The
   * backend validates against the org's real earner roles and returns
   * specific, actionable failure messages (not an earner role / no points
   * configured / would narrow / orphaned scan) — surface those verbatim.
   */
  async widenTargetUserTypes(
    batchId: string,
    targetUserTypes: string[],
  ): Promise<{ couponsReopened: number; targetUserTypes: string[] }> {
    const res = await this.patch(`/batches/${batchId}/target-user-types`, { targetUserTypes });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Gift Service
// ─────────────────────────────────────────────────────────────────────────────

class GiftAdminService extends BaseService<Gift> {
  constructor() {
    super('loyalty/admin/gifts', 'LOYALTY-GIFTS');
  }

  async getGifts(params?: { page?: number; limit?: number; isActive?: boolean }): Promise<PaginatedResponse<Gift>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async createGift(dto: CreateGiftDto): Promise<Gift> {
    const res = await this.post('', dto);
    return res.data ?? res;
  }

  async updateGift(id: string, dto: Partial<CreateGiftDto>): Promise<Gift> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async deleteGift(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }

  async getGiftById(id: string): Promise<Gift> {
    const res = await this.get(`/${id}`);
    return res.data ?? res;
  }

  async toggleActive(id: string, isActive: boolean): Promise<Gift> {
    const res = await this.put(`/${id}`, { isActive });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Cash Reward Slab Service — sub-resource of a Cash-type Gift
// ─────────────────────────────────────────────────────────────────────────────

class CashRewardSlabAdminService extends BaseService<CashRewardSlab> {
  constructor() {
    super('loyalty/admin/gifts', 'LOYALTY-GIFTS');
  }

  async listSlabs(giftId: string): Promise<CashRewardSlab[]> {
    const res = await this.get(`/${giftId}/slabs`);
    return res.data ?? res;
  }

  async createSlab(giftId: string, dto: CreateCashRewardSlabDto): Promise<CashRewardSlab> {
    const res = await this.post(`/${giftId}/slabs`, dto);
    return res.data ?? res;
  }

  async updateSlab(giftId: string, slabId: string, dto: UpdateCashRewardSlabDto): Promise<CashRewardSlab> {
    const res = await this.put(`/${giftId}/slabs/${slabId}`, dto);
    return res.data ?? res;
  }

  async deleteSlab(giftId: string, slabId: string): Promise<void> {
    await this.delete(`/${giftId}/slabs/${slabId}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Redemption Service
// ─────────────────────────────────────────────────────────────────────────────

class RedemptionAdminService extends BaseService<Redemption> {
  constructor() {
    super('loyalty/admin/redemptions', 'LOYALTY-REDEMPTIONS');
  }

  async getRedemptions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    giftType?: GiftType;
    /** Comma-separated list of GiftType values; backend @Transform splits it into an array. */
    giftTypeIn?: string;
  }): Promise<PaginatedResponse<Redemption>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  /**
   * Admin Payout Wallet — running Basiq360 wallet balance (T4).
   * Lives under loyalty/admin/payouts, not loyalty/admin/redemptions, so it
   * goes through `this.client` directly rather than `this.get` (which would
   * prefix `this.baseUrl`).
   */
  async getWalletBalance(): Promise<WalletBalance> {
    const res = await this.client.get('loyalty/admin/payouts/wallet');
    return res.data ?? res;
  }

  /**
   * Admin Payout Wallet — paginated payout ledger rows (T5).
   * NOTE: backend's getWalletLedger (payout.service.ts) returns
   * `{ data: WalletLedgerRow[], total, page, limit }` — a `data`-keyed page,
   * not the `items`-keyed shape `PaginatedResponse<T>` uses elsewhere in this
   * file, so it's typed explicitly here rather than reusing that interface.
   */
  async getWalletLedger(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ data: WalletLedgerEntry[]; total: number; page: number; limit: number }> {
    // Backend responds with { data: rows[], total, page, limit }. this.client.get
    // already unwraps the axios response to that JSON body. The old `res.data ?? res`
    // eagerly returned the rows array (res.data is a truthy array), which broke the
    // caller — PayoutWallet.tsx does `setLedger(resp?.data ?? [])` expecting the
    // envelope, and would get an array whose `.data` is undefined → empty ledger.
    return await this.client.get('loyalty/admin/payouts/ledger', { params });
  }

  async getStats(params?: { giftTypeIn?: string }): Promise<RedemptionStats> {
    const res = await this.get('/stats', { params });
    return res.data ?? res;
  }

  async approve(id: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/approve`, {});
    return res.data ?? res;
  }

  async dispatch(id: string, awbNumber: string, courierPartner: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/dispatch`, { awbNumber, courierPartner });
    return res.data ?? res;
  }

  async reject(id: string, reason: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/reject`, { reason });
    return res.data ?? res;
  }

  async markDelivered(id: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/delivered`, {});
    return res.data ?? res;
  }

  async markAsPaid(id: string, utrNumber: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/mark-paid`, { utrNumber });
    return res.data ?? res;
  }

  async exportPayoutCsv(): Promise<Blob> {
    const res = await this.get('/export-payout', { responseType: 'blob' });
    return res.data ?? res;
  }

  async fulfillVoucher(id: string, voucherCode: string): Promise<Redemption> {
    const res = await this.patch(`/${id}/fulfill-voucher`, { voucherCode });
    return res.data ?? res;
  }

  /**
   * Fire the Basiq360 NEFT payout for a cash-gift redemption. Idempotent
   * server-side — POSTing twice returns the existing URN. See
   * product_loyalty_backend/src/modules/loyalty/payout/payout-admin.controller.ts.
   */
  async sendPayout(redemptionId: string): Promise<SendPayoutResp> {
    const res = await this.post(`/${redemptionId}/send-payout`, {});
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand Report Service — Metcraft onboarding, Feature 4 (2026-08-04)
// ─────────────────────────────────────────────────────────────────────────────

export interface BrandTotalRow {
  /** null = the "Unattributed" bucket (bonus/referral EARN rows with no scanned product) */
  brandId: string | null;
  brandName: string;
  totalPoints: number;
}

export interface CarpenterBrandReportRow {
  /** Not returned by the backend — satisfies BaseService's BaseEntity constraint; use influencerId as the row key. */
  id?: string;
  influencerId: string;
  customerId: string;
  carpenterName: string;
  brandTotals: BrandTotalRow[];
  grandTotal: number;
}

class BrandReportAdminService extends BaseService<CarpenterBrandReportRow> {
  constructor() {
    super('loyalty/admin/reports', 'LOYALTY-BRAND-REPORT');
  }

  async getBrandWisePointsReport(params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<CarpenterBrandReportRow>> {
    const res = await this.get('/brand-wise-points', { params });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fraud Service
// ─────────────────────────────────────────────────────────────────────────────

class FraudAdminService extends BaseService<FlaggedScan> {
  constructor() {
    super('loyalty/admin/fraud', 'LOYALTY-FRAUD');
  }

  async getQueue(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<FlaggedScan>> {
    const res = await this.get('/queue', { params });
    return res.data ?? res;
  }

  async getStats(): Promise<FraudStats> {
    const res = await this.get('/stats');
    return res.data ?? res;
  }

  async reviewScan(scanId: string, action: 'approve' | 'reject', reason?: string): Promise<FlaggedScan> {
    const res = await this.patch(`/scans/${scanId}/review`, { action, reason });
    return res.data ?? res;
  }

  async bulkReview(scanIds: string[], action: 'approve' | 'reject'): Promise<{ processed: number }> {
    const res = await this.post('/bulk-review', { scanIds, action });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// OCR Receipt Service
// ─────────────────────────────────────────────────────────────────────────────

class OcrReceiptAdminService extends BaseService<PurchaseReceipt> {
  constructor() {
    super('loyalty/admin/receipts', 'LOYALTY-RECEIPTS');
  }

  async getPending(params?: { page?: number; limit?: number; status?: string }): Promise<PaginatedResponse<PurchaseReceipt>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async approve(id: string, overrideAmount?: number, overridePoints?: number): Promise<PurchaseReceipt> {
    const res = await this.patch(`/${id}/approve`, { overrideAmount, overridePoints });
    return res.data ?? res;
  }

  async reject(id: string, reason: string): Promise<PurchaseReceipt> {
    const res = await this.patch(`/${id}/reject`, { reason });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Campaign Service
// ─────────────────────────────────────────────────────────────────────────────

class CampaignAdminService extends BaseService<Campaign> {
  constructor() {
    super('loyalty/admin/campaigns', 'LOYALTY-CAMPAIGNS');
  }

  async getCampaigns(params?: { page?: number; limit?: number; status?: string }): Promise<PaginatedResponse<Campaign>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async createCampaign(dto: CreateCampaignDto): Promise<Campaign> {
    const res = await this.post('', dto);
    return res.data ?? res;
  }

  async updateCampaign(id: string, dto: Partial<CreateCampaignDto>): Promise<Campaign> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async activate(id: string | number): Promise<any> {
    return this.patch(`/${id}/activate`, {});
  }

  async pause(id: string): Promise<Campaign> {
    const res = await this.patch(`/${id}/pause`, {});
    return res.data ?? res;
  }

  async end(id: string): Promise<Campaign> {
    const res = await this.delete(`/${id}`);
    return res.data ?? res;
  }

  async getCampaignById(id: string): Promise<Campaign> {
    const res = await this.get(`/${id}`);
    return res.data ?? res;
  }

  async deleteCampaign(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Analytics Service
// ─────────────────────────────────────────────────────────────────────────────

class AnalyticsAdminService extends BaseService {
  constructor() {
    super('loyalty/admin/analytics', 'LOYALTY-ANALYTICS');
  }

  async getOverview(): Promise<AnalyticsOverview> {
    const res = await this.get('/overview');
    const d = res.data ?? res;
    // Map nested API response → flat AnalyticsOverview
    if (d?.influencers || d?.points || d?.activity) {
      return {
        totalInfluencers: d.influencers?.total ?? 0,
        activeInfluencers: d.influencers?.active30d ?? 0,
        newInfluencersThisMonth: d.influencers?.new30d ?? 0,
        pointsInCirculation: d.points?.inCirculation ?? 0,
        totalPointsEarned: d.points?.totalEarned ?? 0,
        totalPointsRedeemed: d.points?.totalRedeemed ?? 0,
        totalPointsExpired: d.points?.totalExpired ?? 0,
        tierDistribution: Object.fromEntries(Object.entries(d.tierDistribution ?? {}).map(([k, v]) => [k.toUpperCase(), v])),
        userTypeDistribution: d.userTypeDistribution ?? {},
        pendingKyc: d.influencers?.pendingKyc ?? Math.max(0, (d.influencers?.total ?? 0) - (d.influencers?.kycApproved ?? 0)),
        pendingRedemptions: d.activity?.pendingRedemptions ?? 0,
        flaggedScans: d.activity?.flaggedScans ?? 0,
      } as AnalyticsOverview;
    }
    return d;
  }

  async getScanTrends(days = 30): Promise<TrendDataPoint[]> {
    const res = await this.get('/scans', { params: { days } });
    const data = res.data ?? res;
    const arr = Array.isArray(data) ? data : data.items ?? [];
    // API returns { date, scans, pointsAwarded } → map to { date, value }
    return arr.map((r: any) => ({ date: r.date, value: r.value ?? r.scans ?? 0 }));
  }

  async getPointsTrends(days = 30): Promise<TrendDataPoint[]> {
    const res = await this.get('/points', { params: { days } });
    const data = res.data ?? res;
    const arr = Array.isArray(data) ? data : data.items ?? [];
    // API returns { date, earned, redeemed } → map to { date, value, value2 }
    return arr.map((r: any) => ({ date: r.date, value: r.value ?? r.earned ?? 0, value2: r.value2 ?? r.redeemed ?? 0 }));
  }

  async getInfluencerStats(): Promise<{
    topEarners: { influencerId: string; name: string; points: number }[];
    retention30d: number;
    retention60d: number;
    retention90d: number;
  }> {
    const res = await this.get('/influencers');
    const d = res.data ?? res;
    // Normalise: backend may return flat retention30d or nested retention.active30d
    return {
      topEarners: (d.topEarners ?? []).map((e: any) => ({
        influencerId: e.influencerId,
        name: e.name ?? e.customerName ?? `INF-${String(e.influencerId).slice(0, 6)}`,
        points: e.points ?? e.totalEarned ?? 0,
      })),
      retention30d: d.retention30d ?? d.retention?.active30d ?? 0,
      retention60d: d.retention60d ?? d.retention?.active60d ?? 0,
      retention90d: d.retention90d ?? d.retention?.active90d ?? 0,
    };
  }

  async getRedemptionTrends(days = 30): Promise<TrendDataPoint[]> {
    const res = await this.get('/redemptions', { params: { days } });
    const data = res.data ?? res;
    const arr = Array.isArray(data) ? data : data.items ?? [];
    return arr.map((r: any) => ({ date: r.date, value: r.value ?? r.redemptions ?? 0, value2: r.value2 ?? r.pointsRedeemed ?? 0 }));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bonus Scheme Service
// ─────────────────────────────────────────────────────────────────────────────

class BonusSchemeAdminService extends BaseService<BonusScheme> {
  constructor() {
    super('loyalty/admin/bonus-schemes', 'LOYALTY-BONUS-SCHEMES');
  }

  async getSchemes(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<BonusScheme>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async createScheme(dto: CreateBonusSchemeDto): Promise<BonusScheme> {
    const res = await this.post('', dto);
    return res.data ?? res;
  }

  async updateScheme(id: string, dto: Partial<CreateBonusSchemeDto>): Promise<BonusScheme> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async getSchemeById(id: string): Promise<BonusScheme> {
    const res = await this.get(`/${id}`);
    return res.data ?? res;
  }

  async deleteScheme(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Targeting Service
// ─────────────────────────────────────────────────────────────────────────────

class TargetingAdminService extends BaseService {
  constructor() {
    super('loyalty/admin/targeting', 'LOYALTY-TARGETING');
  }

  async getScores(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<EngagementScore>> {
    const res = await this.get('/scores', { params });
    return res.data ?? res;
  }

  async getScore(influencerId: string): Promise<EngagementScore> {
    const res = await this.get(`/scores/${influencerId}`);
    return res.data ?? res;
  }

  async getSegments(): Promise<Segment[]> {
    const res = await this.get('/segments');
    const data = res.data ?? res;
    return Array.isArray(data) ? data : data.items ?? [];
  }

  async createSegment(dto: CreateSegmentDto): Promise<Segment> {
    const res = await this.post('/segments', dto);
    return res.data ?? res;
  }

  async getSegmentMembers(segmentId: string, params?: { page?: number; limit?: number }): Promise<{
    influencerIds: string[];
    total: number;
    segmentName: string;
  }> {
    const res = await this.get(`/segments/${segmentId}/members`, { params });
    return res.data ?? res;
  }

  async deleteSegment(segmentId: string): Promise<void> {
    await this.delete(`/segments/${segmentId}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Point Category Service (LOY-010)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `userType` values come from `customer_sub_types.sub_type_code` per tenant, not
 * a fixed set. Widened from a 4-way union to `string` so shape-1 tenants
 * (Kodia=carpenter, ABM Lite=electrician+retailer, Carnival=plumber) plus any
 * future tenant map through without a type change. Canonical per-org list is
 * fetched from the backend via `services/earnerUserTypes.service.ts`
 * (`getEarnerUserTypes`), never hardcoded here.
 */
export interface PointCategoryRule {
  id: string;
  productId: string | null;
  title?: string | null;  // set for category rows (productId = null)
  userType: string;
  basePoints: number;
  isActive: boolean;
}

/** See PointCategoryRule.userType — dynamic per tenant, not a fixed union. */
export interface PointCategoryMatrixRow {
  productId: string;
  userType: string;
  basePoints: number;
}

/**
 * Named category group row. `name` is the group title; every other key is a
 * user_type code mapping to that group's base points for that role. The four
 * legacy Oreno user_types are declared explicitly so existing call sites keep
 * their narrow `number | undefined` typing; the index signature makes room for
 * additional tenant user_types (Kodia: `carpenter`, Carnival: `plumber` — same
 * key as Oreno but originating from a different tenant, and any future ones).
 * See `services/earnerUserTypes.service.ts` (`getEarnerUserTypes`) for how the
 * live set of user_types is resolved. The `boolean` in the index signature only
 * exists so `MatrixRow extends CategoryGroup { _dirty?: boolean }` still compiles.
 */
export interface CategoryGroup {
  name: string;
  electrician?: number;
  plumber?: number;
  counter_boy?: number;
  retailer?: number;
  [userType: string]: number | string | boolean | undefined;
}

export interface ProductWithCategory {
  id: string;
  productCode: string;
  productName: string;
  pointCategoryName: string | null;
}

class PointCategoryAdminService extends BaseService<PointCategoryRule> {
  constructor() {
    super('loyalty/admin/point-categories', 'LOYALTY-POINT-CATEGORY');
  }

  async getRules(productId?: string): Promise<PointCategoryRule[]> {
    const params = productId ? { productId } : undefined;
    const res = await this.get('', { params });
    const data = res.data ?? res;
    return Array.isArray(data) ? data : data.items ?? [];
  }

  async bulkUpsert(rows: PointCategoryMatrixRow[]): Promise<void> {
    await this.post('/bulk', { rows });
  }

  async deleteRule(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }

  async getCategories(): Promise<CategoryGroup[]> {
    const res = await this.get('/categories');
    const data = res.data ?? res;
    return Array.isArray(data) ? data : [];
  }

  async upsertCategoryGroup(data: CategoryGroup): Promise<void> {
    // H-16: backend DTO now accepts a dynamic per-tenant `values` map.
    // Convert the flat CategoryGroup shape (any numeric non-`name` key is a
    // user_type → basePoints entry) into `{ name, values }` so the backend
    // persists Kodia's carpenter / Carnival's plumber / ABM's user_types
    // instead of dropping them. Legacy flat fields are still accepted server
    // side for BC, but we now send the canonical shape.
    const { name, ...rest } = data;
    const values: Record<string, number> = {};
    for (const [k, v] of Object.entries(rest)) {
      if (typeof v === 'number') values[k] = v;
    }
    await this.post('/categories', { name, values });
  }

  async deleteCategoryGroup(name: string): Promise<void> {
    await this.delete(`/categories/${encodeURIComponent(name)}`);
  }

  async getProductsWithCategory(params: { page?: number; limit?: number; search?: string }): Promise<{ items: ProductWithCategory[]; total: number }> {
    const res = await this.get('/products', { params });
    const data = res.data ?? res;
    return data;
  }

  async assignProductCategory(productId: string, categoryName: string | null): Promise<void> {
    await this.patch(`/products/${productId}/assign`, { categoryName });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Referral Service
// ─────────────────────────────────────────────────────────────────────────────

class ReferralAdminService extends BaseService {
  constructor() {
    super('loyalty/referrals', 'LOYALTY-REFERRALS');
  }

  async getReferrals(params?: { page?: number; limit?: number; status?: string }): Promise<ReferralAdminResponse> {
    const res = await this.get('/admin', { params });
    return res.data ?? res;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Singletons (follow SFA pattern)
// ─────────────────────────────────────────────────────────────────────────────

export const influencerAdminService = new InfluencerAdminService();
export const couponAdminService = new CouponAdminService();
export const giftAdminService = new GiftAdminService();
export const cashRewardSlabAdminService = new CashRewardSlabAdminService();
export const redemptionAdminService = new RedemptionAdminService();
export const brandReportAdminService = new BrandReportAdminService();
export const fraudAdminService = new FraudAdminService();
export const ocrReceiptAdminService = new OcrReceiptAdminService();
export const campaignAdminService = new CampaignAdminService();
export const bonusSchemeAdminService = new BonusSchemeAdminService();
export const targetingAdminService = new TargetingAdminService();
export const analyticsAdminService = new AnalyticsAdminService();
export const pointCategoryAdminService = new PointCategoryAdminService();
export const referralAdminService = new ReferralAdminService();

// ─────────────────────────────────────────────────────────────────────────────
// Games Admin Service
// ─────────────────────────────────────────────────────────────────────────────

class GameAdminService extends BaseService<GameConfig> {
  constructor() {
    super('loyalty/admin/games', 'GAMES');
  }

  async getGames(): Promise<GameConfig[]> {
    const res = await this.get('');
    return res.data ?? res;
  }

  async createGame(dto: CreateGameDto): Promise<GameConfig> {
    const res = await this.post('', dto);
    return res.data ?? res;
  }

  async updateGame(id: string, dto: Partial<CreateGameDto> & { isActive?: boolean }): Promise<GameConfig> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async deleteGame(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }
}

export const gameAdminService = new GameAdminService();

// ─────────────────────────────────────────────────────────────────────────────
// Tier Config Admin Service (LOY-005)
// ─────────────────────────────────────────────────────────────────────────────

class TierConfigAdminService extends BaseService<TierConfig> {
  constructor() {
    super('loyalty/admin/tier-configs', 'TIER_CONFIG');
  }

  async getTierConfigs(): Promise<TierConfig[]> {
    const res = await this.get('');
    return Array.isArray(res) ? res : (res.data ?? []);
  }

  async updateTierConfig(id: string, dto: UpdateTierConfigDto): Promise<TierConfig> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async seedDefaults(): Promise<{ message: string; count: number; configs: TierConfig[] }> {
    const res = await this.post('/seed', {});
    return res.data ?? res;
  }
}

export const tierConfigAdminService = new TierConfigAdminService();

// ─────────────────────────────────────────────────────────────────────────────
// Leaderboard Service
// ─────────────────────────────────────────────────────────────────────────────

export type LeaderboardPeriod = 'weekly' | 'monthly' | 'all_time';

export interface LeaderboardEntry {
  rank: number;
  influencerId: string | number;
  name: string;
  mobile?: string;
  points: number;
  tier?: string;
}

class LeaderboardService extends BaseService {
  constructor() {
    super('loyalty/leaderboard', 'LOYALTY-LEADERBOARD');
  }

  async getLeaderboard(period: LeaderboardPeriod): Promise<LeaderboardEntry[]> {
    const res = await this.get('', { params: { period } });
    const data = res.data ?? res;
    const arr = Array.isArray(data) ? data : data.entries ?? data.items ?? data.data ?? [];
    return arr.map((entry: any, idx: number) => ({
      rank: entry.rank ?? idx + 1,
      influencerId: entry.influencerId ?? entry.id ?? idx,
      name: entry.name ?? entry.customerName ?? entry.influencerName ?? '-',
      mobile: entry.mobile ?? entry.phone ?? '-',
      points: entry.points ?? entry.totalPoints ?? entry.totalEarned ?? 0,
      tier: entry.tier ?? undefined,
    }));
  }
}

export const leaderboardService = new LeaderboardService();

// ─────────────────────────────────────────────────────────────────────────────
// Work Showcase Admin Service (LOY-030)
// ─────────────────────────────────────────────────────────────────────────────

class WorkShowcaseAdminService extends BaseService<WorkShowcase> {
  constructor() {
    super('loyalty/admin/work-showcase', 'WORK-SHOWCASE');
  }

  async getStats(): Promise<WorkShowcaseStats> {
    const res = await this.get('/stats');
    return res.data ?? res;
  }

  async fetchList(params: WorkShowcaseListParams): Promise<{ items: WorkShowcase[]; total: number }> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async approve(id: string, pointsToAward?: number): Promise<WorkShowcase> {
    const res = await this.patch(`/${id}/approve`, { pointsToAward });
    return res.data ?? res;
  }

  async reject(id: string, reason: string): Promise<WorkShowcase> {
    const res = await this.patch(`/${id}/reject`, { reason });
    return res.data ?? res;
  }

  async toggleFeatured(id: string): Promise<WorkShowcase> {
    const res = await this.patch(`/${id}/feature`, {});
    return res.data ?? res;
  }
}

export const workShowcaseAdminService = new WorkShowcaseAdminService();

// ─────────────────────────────────────────────────────────────────────────────
// KYC Admin Queue Service (LOY-003)
// ─────────────────────────────────────────────────────────────────────────────

export interface KycQueueItem {
  documentId: string;
  influencerId: string;
  customerName: string;
  mobile: string;
  documentType: string;
  ocrExtractedName: string;
  ocrConfidence: number;
  documentUrl: string;
  submittedAt: string;
  /** Set when document was verified via the middleware-routed digital pipeline. */
  digitalMethod?: 'aadhaar_okyc' | 'pan_basic' | 'pan_name_match' | 'bank_pennydrop' | 'other' | null;
  digitalStatus?: 'pending' | 'in_progress' | 'verified' | 'failed' | 'skipped' | null;
  digitalVerifiedAt?: string | null;
}

export interface KycQueuePage {
  items: KycQueueItem[];
  total: number;
  page: number;
  limit: number;
}

class KycAdminQueueService extends BaseService {
  constructor() {
    super('loyalty/admin/kyc', 'LOYALTY_FRAUD_REVIEW');
  }

  async getReviewQueue(params?: { page?: number; limit?: number; documentType?: string }): Promise<KycQueuePage> {
    const res = await this.get('/review-queue', { params });
    return res.data ?? res;
  }

  async approveDocument(documentId: string, notes?: string): Promise<void> {
    await this.patch(`/${documentId}/approve`, { notes });
  }

  async rejectDocument(documentId: string, rejectionReason: string): Promise<void> {
    await this.patch(`/${documentId}/reject`, { rejectionReason });
  }
}

export const kycAdminQueueService = new KycAdminQueueService();

// ─────────────────────────────────────────────────────────────────────────────
// Manual Approval Admin Service (Kodia Plan 05 — LOYALTY_MANUAL_APPROVAL)
// Backend endpoints: /loyalty/admin/manual-approval/*
// Note: shape below mirrors plan §5.1 — actual backend lands in a parallel commit.
// ─────────────────────────────────────────────────────────────────────────────

export interface ManualApprovalDocument {
  type: string;
  signedUrl: string;
  mimeType: string;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED';
  documentNumber?: string | null;
}

export interface ManualApprovalQueueItem {
  id: string;
  customerCode?: string;
  customerName: string;
  mobile: string;
  loginTypeId: number;
  subTypeId?: number | null;
  submittedAt: string;
  documents: ManualApprovalDocument[];
}

export interface ManualApprovalQueuePage {
  items: ManualApprovalQueueItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ManualApprovalDetail {
  id: string;
  customerCode?: string;
  customerName: string;
  mobile: string;
  email?: string | null;
  loginTypeId: number;
  subTypeId?: number | null;
  submittedAt: string;
  address?: string | null;
  aadhaarNumberMasked?: string | null;
  panNumber?: string | null;
  documents: ManualApprovalDocument[];
}

export interface ManualApprovalListParams {
  page?: number;
  limit?: number;
  search?: string;
  loginTypeId?: number;
}

// ─── Additional-Documents-Pending queue (Task #42) ─────────────────────────
// After a customer has been approved by admin, some doc types may still be
// left in a non-VERIFIED state (e.g. optional docs uploaded after approval,
// or docs that reverted to PENDING via a manual re-request). This queue
// surfaces those rows so ops can drive them to closure from the Influencer
// Network detail page.

export interface AdditionalDocPending {
  type: string;
  verificationStatus: 'PENDING' | 'REJECTED' | 'EXPIRED';
  documentNumber?: string | null;
  submittedAt?: string | null;
  signedUrl?: string | null;
}

export interface AdditionalDocsPendingItem {
  id: string;
  customerCode?: string;
  customerName: string;
  mobile: string;
  loginTypeId: number;
  subTypeId?: number | null;
  approvedAt: string;
  pendingDocs: AdditionalDocPending[];
}

export interface AdditionalDocsPendingPage {
  items: AdditionalDocsPendingItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdditionalDocsPendingListParams {
  page?: number;
  limit?: number;
  search?: string;
}

class ManualApprovalAdminService extends BaseService {
  constructor() {
    super('loyalty/admin/manual-approval', 'LOYALTY_MANUAL_APPROVAL');
  }

  async getPending(params?: ManualApprovalListParams): Promise<ManualApprovalQueuePage> {
    const res = await this.get('/pending', { params });
    return res.data ?? res;
  }

  async getAdditionalDocsPending(
    params?: AdditionalDocsPendingListParams,
  ): Promise<AdditionalDocsPendingPage> {
    const res = await this.get('/additional-docs-pending', { params });
    return res.data ?? res;
  }

  async getDetail(customerId: string): Promise<ManualApprovalDetail> {
    const res = await this.get(`/${customerId}`);
    return res.data ?? res;
  }

  async approve(customerId: string): Promise<void> {
    await this.post(`/${customerId}/approve`, {});
  }

  async reject(customerId: string, reason: string): Promise<void> {
    await this.post(`/${customerId}/reject`, { reason });
  }

  async getPendingCount(): Promise<{ count: number }> {
    const res = await this.get('/pending-count');
    return res.data ?? res;
  }
}

export const manualApprovalAdminService = new ManualApprovalAdminService();

// ─────────────────────────────────────────────────────────────────────────────
// Wallet Self-Service (field-user "My Wallet" — Point History page)
// ─────────────────────────────────────────────────────────────────────────────

class WalletSelfService extends BaseService<PointTransaction> {
  constructor() {
    super('loyalty/wallet', 'LOYALTY-WALLET-SELF');
  }

  async getMyTransactions(page = 1, limit = 20): Promise<PaginatedResponse<PointTransaction>> {
    const res = await this.get('/transactions', { params: { page, limit } });
    return res.data ?? res;
  }
}

export const walletService = new WalletSelfService();

// ─────────────────────────────────────────────────────────────────────────────
// Label Template Admin Service (Sticker Template Designer — Phase 1)
// Backend endpoints: /loyalty/admin/label-templates/*
// See docs/specs/2026-07-25-sticker-template-designer.md
// ─────────────────────────────────────────────────────────────────────────────

class LabelTemplateAdminService extends BaseService<LabelTemplate> {
  constructor() {
    super('loyalty/admin/label-templates', 'LOYALTY_LABEL_TEMPLATE');
  }

  async getTemplates(params?: LabelTemplateListParams): Promise<PaginatedResponse<LabelTemplate>> {
    const res = await this.get('', { params });
    return res.data ?? res;
  }

  async getTemplate(id: string): Promise<LabelTemplate> {
    const res = await this.get(`/${id}`);
    return res.data ?? res;
  }

  /** Alias for `getTemplates` — the coupon-batch template picker (CouponBatchAdd) reads this name. */
  async getAll(params?: LabelTemplateListParams): Promise<PaginatedResponse<LabelTemplate>> {
    return this.getTemplates(params);
  }

  /** Alias for `getTemplate` — LabelPrintDialog reads this name when a batch has a specific template. */
  async getOne(id: string): Promise<LabelTemplate> {
    return this.getTemplate(id);
  }

  /**
   * Returns the org's current default template, or throws (404) if none is
   * configured. Callers should catch and fall back to the hardcoded
   * `labelConfigs.ts` — a fresh tenant without a template must still print.
   */
  async getDefault(): Promise<LabelTemplate> {
    const res = await this.get('/default');
    return res.data ?? res;
  }

  async createTemplate(dto: CreateLabelTemplateDto): Promise<LabelTemplate> {
    const res = await this.post('', dto);
    return res.data ?? res;
  }

  async updateTemplate(id: string, dto: UpdateLabelTemplateDto): Promise<LabelTemplate> {
    const res = await this.put(`/${id}`, dto);
    return res.data ?? res;
  }

  async setDefault(id: string): Promise<LabelTemplate> {
    const res = await this.post(`/${id}/set-default`, {});
    return res.data ?? res;
  }

  async deleteTemplate(id: string): Promise<void> {
    await this.delete(`/${id}`);
  }

  /**
   * Uploads a logo/image asset used by IMAGE elements. Backend returns the
   * canonical `imageUrl` we then persist on the element via updateTemplate.
   */
  async uploadImage(
    file: File,
  ): Promise<{ imageUrl: string; mimeType: string; fileSize: number }> {
    const formData = new FormData();
    formData.append('file', file);
    // BaseService.uploadFile handles Content-Type / multipart wrangling.
    const res = await (this as any).uploadFile('/upload-image', formData);
    return res.data ?? res;
  }
}

export const labelTemplateAdminService = new LabelTemplateAdminService();
